import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { AVATAR_ASSETS, BRAND_COLORS } from '@lk/shared';
import { cn } from '../lib/cn';
import { useUiStore } from '../store/uiStore';
import { useLogout } from '../features/auth/useAuth';
import { useSyncState } from '../offline/useSync';
import { BrandImg } from './BrandImg';
import { Icon, avatarIcon } from './Icon';
import { SyncIndicator } from './SyncIndicator';

export interface TopBarProps {
  title?: string;
  avatarKey?: string;
  name?: string;
  back?: { to: string; label?: string };
  right?: React.ReactNode;
  className?: string;
  /** Показати кнопку виходу. Вимикається там, де вихід уже є в макеті. */
  showLogout?: boolean;
}

export function TopBar({
  title,
  avatarKey,
  name,
  back,
  right,
  className,
  showLogout = true,
}: TopBarProps): React.JSX.Element {
  const soundEnabled = useUiStore((s) => s.soundEnabled);
  const toggleSound = useUiStore((s) => s.toggleSound);

  return (
    <header className={cn('flex items-center gap-3 py-3', className)}>
      {back ? (
        <Link to={back.to} className="lk-chip shrink-0" aria-label={back.label ?? 'Назад'}>
          <Icon name="arrow-left" size={18} />
        </Link>
      ) : null}

      {avatarKey ? (
        <BrandImg
          src={AVATAR_ASSETS[avatarKey]?.path ?? `/brand/avatars/avatar-${avatarKey}.png`}
          alt={name ?? 'Профіль'}
          fallbackIcon={avatarIcon(avatarKey)}
          colors={[BRAND_COLORS.cyan, BRAND_COLORS.magenta]}
          shape="circle"
          className="h-11 w-11 shrink-0"
          eager
        />
      ) : null}

      <div className="min-w-0 flex-1">
        {title ? <h1 className="truncate text-lg font-black">{title}</h1> : null}
        {name && !title ? <p className="truncate text-lg font-black">{name}</p> : null}
      </div>

      {right}

      <button
        type="button"
        onClick={toggleSound}
        className="lk-chip shrink-0 cursor-pointer"
        aria-pressed={soundEnabled}
        aria-label={soundEnabled ? 'Вимкнути звук' : 'Увімкнути звук'}
        title={soundEnabled ? 'Звук увімкнено' : 'Звук вимкнено'}
      >
        <Icon name={soundEnabled ? 'sound-on' : 'sound-off'} size={18} />
      </button>

      {showLogout ? <LogoutButton /> : null}

      <SyncIndicator className="shrink-0" />
    </header>
  );
}

/**
 * Вихід із акаунта.
 *
 * З підтвердженням навмисно: у дитини цей рядок завжди на екрані, і один
 * випадковий тап посеред сесії викидав би її на екран PIN. Дані при цьому
 * не губляться — черга спроб переживає вихід, — але прикрощів вистачить.
 *
 * Якщо є неподані відповіді, попереджаємо окремо: вони доїдуть після
 * наступного входу, і дитина має це бачити, а не здогадуватись.
 */
function LogoutButton(): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const logout = useLogout();
  const navigate = useNavigate();
  // `unsent` — саме те число, яке бачить дитина в індикаторі синхронізації.
  const { unsent } = useSyncState();

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="lk-chip shrink-0 cursor-pointer"
        aria-label="Вийти з акаунта"
        title="Вийти"
        data-testid="logout-button"
      >
        <Icon name="exit" size={18} />
      </button>
    );
  }

  return (
    <div
      className="flex shrink-0 items-center gap-2"
      role="group"
      aria-label="Підтвердження виходу"
    >
      {unsent > 0 ? (
        <span className="hidden text-[11px] font-bold text-white/60 sm:inline">
          {unsent} не надіслано
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => {
          // `expired: false` — це звичайний вихід, а не протухла сесія:
          // екран входу не має лякати дитину повідомленням про втрачену сесію.
          logout.mutate(undefined, {
            onSettled: () => void navigate({ to: '/login', search: { expired: false } }),
          });
        }}
        className="lk-chip shrink-0 cursor-pointer border-[var(--lk-red)]/60 text-[11px] font-black"
        disabled={logout.isPending}
        data-testid="logout-confirm"
      >
        Вийти
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="lk-chip shrink-0 cursor-pointer"
        aria-label="Скасувати вихід"
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
